import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { Pagination } from "@/types";
import { BaseOrganization, Organization } from "@/types/organizations";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useWorkspaces } from ".";

const useOrganization = (organizationId: number) => {
  const userToken = useAuth()?.userToken!;
  const url = new URL(`${ENDPOINTS.organizations}/${organizationId}`);
  return useQuery(
    [ENDPOINTS.organizations, organizationId],
    (): Promise<Organization> =>
      fetch(url, {
        method: "GET",
        headers: jsonAuthHeaders(userToken),
      }).then(handleResponse),
    {
      enabled: !!organizationId,
    },
  );
};

const useOrganizations = () => {
  const userToken = useAuth()?.userToken!;
  const url = new URL(ENDPOINTS.organizations);
  return useQuery(
    [ENDPOINTS.organizations],
    (): Promise<Pagination<Organization>> =>
      fetch(url, {
        method: "GET",
        headers: jsonAuthHeaders(userToken),
      }).then(handleResponse),
  );
};

const useCreateOrganization = () => {
  const userToken = useAuth()?.userToken!;
  const queryClient = useQueryClient();
  const { refetch } = useWorkspaces();
  const navigate = useNavigate();

  return useMutation(
    (body: BaseOrganization): Promise<Organization> =>
      fetch(ENDPOINTS.organizations, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(body),
      }).then(handleResponse),
    {
      onSuccess: async (newOrganization) => {
        queryClient.invalidateQueries([ENDPOINTS.organizations]);
        const { data } = await refetch();
        const workspace = data?.workspaces.find(
          (workspace) => workspace.organization_id === newOrganization.id,
        );
        if (workspace) navigate(`/workspace/${workspace.id}`);
      },
    },
  );
};

const useEditOrganization = (userToken: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ organizationId, name }: { organizationId: number; name: string }) =>
      fetch(`${ENDPOINTS.organizations}/${organizationId}`, {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          name,
        }),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.organizations]);
        displayToast("Organization name updated", ToastType.success);
      },
      onError: () => {
        displayErrorToast("Failed to update organization name");
      },
    },
  );
};

export {
  useCreateOrganization,
  useEditOrganization,
  useOrganization,
  useOrganizations,
};
